import type { ColorThemeName, ColorBaseToken, IconUsageToken } from "../../design-tokens";
import {
  getColorBaseTokenValue,
  getIconFamilyTokenValue,
  getIconGradeTokenValue,
  getIconWeightTokenValue,
  getRadiusTokenValue,
  getSpacingTokenValue,
  resolveColorTheme,
  resolveIconStateToken,
  resolveIconUsageToken,
  resolveTextToken
} from "../../design-tokens";

export type ThemeCssVariables = Record<string, string>;

const buildSemanticColorVariables = (themeName: ColorThemeName): ThemeCssVariables => {
  const resolvedColors = resolveColorTheme(themeName);

  return {
    "--color-ui-bg-default": resolvedColors["color.ui.bg.default"],
    "--color-ui-bg-surface": resolvedColors["color.ui.bg.surface"],
    "--color-ui-bg-subtle": resolvedColors["color.ui.bg.subtle"],
    "--color-ui-bg-hover": resolvedColors["color.ui.bg.hover"],
    "--color-ui-text-primary": resolvedColors["color.ui.text.primary"],
    "--color-ui-text-secondary": resolvedColors["color.ui.text.secondary"],
    "--color-ui-text-disabled": resolvedColors["color.ui.text.disabled"],
    "--color-ui-text-inverse": resolvedColors["color.ui.text.inverse"],
    "--color-ui-icon-primary": resolvedColors["color.ui.icon.primary"],
    "--color-ui-icon-secondary": resolvedColors["color.ui.icon.secondary"],
    "--color-ui-action-primary": resolvedColors["color.ui.action.primary"],
    "--color-ui-action-secondary": resolvedColors["color.ui.action.secondary"],
    "--color-ui-border-default": resolvedColors["color.ui.border.default"],
    "--color-ui-border-secondary": resolvedColors["color.ui.border.secondary"],
    "--color-ui-border-subtle": resolvedColors["color.ui.border.subtle"],
    "--color-ui-border-hover": resolvedColors["color.ui.border.hover"],
    "--color-state-hover": resolvedColors["color.state.hover"],
    "--color-state-active": resolvedColors["color.state.active"],
    "--color-state-focus": resolvedColors["color.state.focus"],
    "--color-state-disabled": resolvedColors["color.state.disabled"],
    "--color-feedback-success-100": getColorBaseTokenValue("color.feedback.success.100"),
    "--color-feedback-success-500": getColorBaseTokenValue("color.feedback.success.500"),
    "--color-feedback-success-700": getColorBaseTokenValue("color.feedback.success.700"),
    "--color-feedback-success-900": getColorBaseTokenValue("color.feedback.success.900"),
    "--color-feedback-warning-100": getColorBaseTokenValue("color.feedback.warning.100"),
    "--color-feedback-warning-500": getColorBaseTokenValue("color.feedback.warning.500"),
    "--color-feedback-warning-700": getColorBaseTokenValue("color.feedback.warning.700"),
    "--color-feedback-warning-900": getColorBaseTokenValue("color.feedback.warning.900"),
    "--color-feedback-danger-100": getColorBaseTokenValue("color.feedback.danger.100"),
    "--color-feedback-danger-500": getColorBaseTokenValue("color.feedback.danger.500"),
    "--color-feedback-danger-700": getColorBaseTokenValue("color.feedback.danger.700"),
    "--color-feedback-danger-900": getColorBaseTokenValue("color.feedback.danger.900"),
    "--color-secondary-100": getColorBaseTokenValue("color.secondary.100"),
    "--color-secondary-300": getColorBaseTokenValue("color.secondary.300"),
    "--color-secondary-500": getColorBaseTokenValue("color.secondary.500"),
    "--color-secondary-700": getColorBaseTokenValue("color.secondary.700"),
    "--color-secondary-900": getColorBaseTokenValue("color.secondary.900"),
    "--color-primary-100": getColorBaseTokenValue("color.primary.100"),
    "--color-primary-300": getColorBaseTokenValue("color.primary.300"),
    "--color-primary-500": getColorBaseTokenValue("color.primary.500"),
    "--color-primary-700": getColorBaseTokenValue("color.primary.700"),
    "--color-primary-900": getColorBaseTokenValue("color.primary.900"),
    "--color-neutral-000": getColorBaseTokenValue("color.neutral.000"),
    "--color-neutral-100": getColorBaseTokenValue("color.neutral.100"),
    "--color-neutral-300": getColorBaseTokenValue("color.neutral.300"),
    "--color-neutral-500": getColorBaseTokenValue("color.neutral.500"),
    "--color-neutral-700": getColorBaseTokenValue("color.neutral.700"),
    "--color-neutral-900": getColorBaseTokenValue("color.neutral.900"),
    // Aliases para shades intermediários (não existem no sistema de tokens, mapeados ao mais próximo)
    "--color-neutral-025": getColorBaseTokenValue("color.neutral.100"),
    "--color-neutral-050": getColorBaseTokenValue("color.neutral.100"),
    "--color-neutral-200": getColorBaseTokenValue("color.neutral.300"),
    "--color-neutral-400": getColorBaseTokenValue("color.neutral.300"),
    "--color-neutral-600": getColorBaseTokenValue("color.neutral.700"),
    "--color-neutral-800": getColorBaseTokenValue("color.neutral.700"),
    "--color-primary-050": getColorBaseTokenValue("color.primary.100"),
    "--color-primary-200": getColorBaseTokenValue("color.primary.300"),
    "--color-primary-600": getColorBaseTokenValue("color.primary.500"),
    "--color-feedback-warning-600": getColorBaseTokenValue("color.feedback.warning.700"),
    // Aliases para vars não-padrão usadas em componentes legados
    "--color-surface": getColorBaseTokenValue("color.neutral.000"),
    "--color-text-primary": resolvedColors["color.ui.text.primary"],
    "--border-default": resolvedColors["color.ui.border.default"],
    "--shadow-sm": "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "--shadow-md": "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "--border-radius-md": "var(--radius-md)",
  };
};

const buildTypographyVariables = (): ThemeCssVariables => {
  const heading = resolveTextToken("text.heading.page");
  const bodyPrimary = resolveTextToken("text.body.primary");
  const bodySecondary = resolveTextToken("text.body.secondary");
  const label = resolveTextToken("text.ui.label");
  const caption = resolveTextToken("text.ui.caption");

  return {
    "--font-family-heading": heading.fontFamily,
    "--font-family-base": bodyPrimary.fontFamily,
    "--font-size-heading-page": heading.fontSize,
    "--font-size-heading-section": resolveTextToken("text.heading.section").fontSize,
    "--font-size-heading-subsection": resolveTextToken("text.heading.subsection").fontSize,
    "--font-size-body-primary": bodyPrimary.fontSize,
    "--font-size-body-secondary": bodySecondary.fontSize,
    "--font-size-label": label.fontSize,
    "--font-size-caption": caption.fontSize,
    "--font-weight-regular": bodyPrimary.fontWeight.toString(),
    "--font-weight-medium": label.fontWeight.toString(),
    "--font-weight-semibold": heading.fontWeight.toString(),
    "--line-height-tight": heading.lineHeight.toString(),
    "--line-height-normal": bodyPrimary.lineHeight.toString()
  };
};

const buildShapeVariables = (): ThemeCssVariables => {
  const spacing2xs = getSpacingTokenValue("spacing.2xs");
  const spacingXs = getSpacingTokenValue("spacing.xs");
  const spacingSm = getSpacingTokenValue("spacing.sm");
  const spacingMd = getSpacingTokenValue("spacing.md");
  const spacingLg = getSpacingTokenValue("spacing.lg");
  const spacingXl = getSpacingTokenValue("spacing.xl");

  const radiusNone = getRadiusTokenValue("radius.none");
  const radiusXs = getRadiusTokenValue("radius.xs");
  const radiusSm = getRadiusTokenValue("radius.sm");
  const radiusMd = getRadiusTokenValue("radius.md");
  const radiusLg = getRadiusTokenValue("radius.lg");
  const radiusXl = getRadiusTokenValue("radius.xl");
  const radiusFull = getRadiusTokenValue("radius.full");

  return {
    "--space-2xs": spacing2xs,
    "--space-xs": spacingXs,
    "--space-sm": spacingSm,
    "--space-md": spacingMd,
    "--space-lg": spacingLg,
    "--space-xl": spacingXl,
    "--radius-none": radiusNone,
    "--radius-xs": radiusXs,
    "--radius-sm": radiusSm,
    "--radius-md": radiusMd,
    "--radius-lg": radiusLg,
    "--radius-xl": radiusXl,
    "--radius-full": radiusFull,
    "--border-width-hairline": "1px"
  };
};

const buildIconVariables = (): ThemeCssVariables => {
  const family = getIconFamilyTokenValue("icon.family.material");
  const weight = getIconWeightTokenValue("icon.weight.regular");
  const grade = getIconGradeTokenValue("icon.grade.default");

  const defaultState = resolveIconStateToken("icon.state.default");
  const hoverState = resolveIconStateToken("icon.state.hover");
  const activeState = resolveIconStateToken("icon.state.active");
  const disabledState = resolveIconStateToken("icon.state.disabled");

  const resolveUsage = (token: IconUsageToken): { fontSize: string; opticalSize: string } => {
    const resolved = resolveIconUsageToken(token);
    return {
      fontSize: resolved.fontSize,
      opticalSize: resolved.opticalSize.toString()
    };
  };

  const secondary = resolveUsage("icon.usage.secondary-action");
  const listing = resolveUsage("icon.usage.listing");
  const button = resolveUsage("icon.usage.button");
  const card = resolveUsage("icon.usage.card");
  const kpi = resolveUsage("icon.usage.kpi");
  const kpiEmphasis = resolveUsage("icon.usage.kpi-emphasis");

  return {
    "--icon-font-family": `'${family}'`,
    "--icon-font-weight": weight.toString(),
    "--icon-font-grade": grade.toString(),
    "--icon-fill-default": defaultState.fill.toString(),
    "--icon-fill-hover": hoverState.fill.toString(),
    "--icon-fill-active": activeState.fill.toString(),
    "--icon-fill-disabled": disabledState.fill.toString(),
    "--icon-opacity-disabled": disabledState.opacity.toString(),
    "--icon-size-xs": secondary.fontSize,
    "--icon-size-xs-opsz": secondary.opticalSize,
    "--icon-size-sm": listing.fontSize,
    "--icon-size-sm-opsz": listing.opticalSize,
    "--icon-size-md": button.fontSize,
    "--icon-size-md-opsz": button.opticalSize,
    "--icon-size-lg": kpi.fontSize,
    "--icon-size-lg-opsz": kpi.opticalSize,
    "--icon-size-xl": kpiEmphasis.fontSize,
    "--icon-size-xl-opsz": kpiEmphasis.opticalSize,
  "--icon-size-default": button.fontSize,
  "--icon-size-default-opsz": button.opticalSize,
    "--icon-size-secondary-action": secondary.fontSize,
    "--icon-size-secondary-action-opsz": secondary.opticalSize,
    "--icon-size-listing": listing.fontSize,
    "--icon-size-listing-opsz": listing.opticalSize,
    "--icon-size-button": button.fontSize,
    "--icon-size-button-opsz": button.opticalSize,
    "--icon-size-card": card.fontSize,
    "--icon-size-card-opsz": card.opticalSize,
    "--icon-size-kpi": kpi.fontSize,
    "--icon-size-kpi-opsz": kpi.opticalSize,
    "--icon-size-kpi-emphasis": kpiEmphasis.fontSize,
    "--icon-size-kpi-emphasis-opsz": kpiEmphasis.opticalSize
  };
};

const buildPrimaryPaletteVariables = (): ThemeCssVariables => {
  const primaryShades: Record<string, ColorBaseToken> = {
    "--primary-50": "color.primary.100",
    "--primary-100": "color.primary.100",
    "--primary-200": "color.primary.300",
    "--primary-300": "color.primary.300",
    "--primary-400": "color.primary.500",
    "--primary-500": "color.primary.500",
    "--primary-600": "color.primary.700",
    "--primary-700": "color.primary.700",
    "--primary-800": "color.primary.900",
    "--primary-900": "color.primary.900"
  };

  return Object.entries(primaryShades).reduce<ThemeCssVariables>((accumulator, [cssVar, token]) => {
    accumulator[cssVar] = getColorBaseTokenValue(token);
    return accumulator;
  }, {});
};

export const buildThemeVariables = (themeName: ColorThemeName): ThemeCssVariables => ({
  ...buildSemanticColorVariables(themeName),
  ...buildTypographyVariables(),
  ...buildShapeVariables(),
  ...buildIconVariables()
});

export const resolvePrimeReactTokens = (
  themeName: ColorThemeName
): Record<string, string> => {
  const neutral900 = getColorBaseTokenValue("color.neutral.900");
  const [r, g, b] = neutral900.match(/\w\w/g)!.map(x => parseInt(x, 16));
  const maskBackground = themeName === "dark" ? `rgba(${r}, ${g}, ${b}, 0.72)` : `rgba(${r}, ${g}, ${b}, 0.56)`;

  return {
    ...buildPrimaryPaletteVariables(),
    "--primary-color": "var(--color-ui-action-primary)",
    "--primary-color-text": "var(--color-ui-text-inverse)",
    "--surface-ground": "var(--color-ui-bg-default)",
    "--surface-section": "var(--color-ui-bg-surface)",
    "--surface-card": "var(--color-ui-bg-surface)",
    "--surface-hover": "var(--color-ui-bg-subtle)",
    "--surface-border": "var(--color-ui-border-default)",
    "--surface-border-light": "var(--color-ui-border-subtle)",
    "--text-color": "var(--color-ui-text-primary)",
    "--text-color-secondary": "var(--color-ui-text-secondary)",
    "--text-color-hover": "var(--color-ui-text-primary)",
    "--text-color-disabled": "var(--color-state-disabled)",
    "--focus-ring": "0 0 0 0.2rem var(--color-state-focus)",
    "--highlight-bg": "var(--color-ui-bg-subtle)",
    "--highlight-text-color": "var(--color-ui-text-primary)",
    "--border-radius": "var(--radius-md)",
    "--overlay-background": "var(--color-ui-bg-surface)",
    "--mask-background": maskBackground,
    "--success-color": "var(--color-feedback-success-500)",
    "--warning-color": "var(--color-feedback-warning-500)",
    "--danger-color": "var(--color-feedback-danger-500)"
  };
};

export const mergeThemeVariables = (
  themeName: ColorThemeName
): ThemeCssVariables => ({
  ...buildThemeVariables(themeName),
  ...resolvePrimeReactTokens(themeName)
});

export const mapToCssVariables = (variables: ThemeCssVariables): string =>
  Object.entries(variables)
    .map(([name, value]) => `${name}: ${value};`)
    .join(" ");
